import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScenarioRunClient, ScenarioRunList, ScenarioSuiteSummary } from '../../api/api';
import { checkScenarioPreflight, ScenarioPreflightReport } from '../../api/scenarioPreflight';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useCockpit } from '../data/CockpitContext';
import { ago, duration, shortBuild, when } from '../data/format';
import { apiError, usePoll } from '../data/usePoll';
import { Card, CheckRow, CheckTone, Empty, EnvChip, ErrorNotice, Kv, Loading, Spinner } from '../components/ui';
import { v2 } from '../paths';

/** Red when something failed, green only when every scenario passed; a run stopped halfway is neither. */
const runTone = (failed?: number, finished?: string, passed = 0, total = 0) =>
    failed ? 'chip bad' : !finished ? 'chip info' : passed >= total ? 'chip ok' : 'chip';

const preflightTone = (status?: string): CheckTone => {
    const s = (status ?? '').toLowerCase();
    return s === 'passed' || s === 'ok' ? 'ok' : s === 'failed' || s === 'blocked' ? 'bad' : s === 'warning' || s === 'warn' ? 'warn' : 'wait';
};

const Segments = ({ passed = 0, failed = 0, total = 0 }: { passed?: number; failed?: number; total?: number }) => (
    <span className="segs" style={{ minWidth: 120 }}>
        {Array.from({ length: Math.min(total, 30) }, (_, i) => (
            <i key={i} className={i < passed ? 'done' : i < passed + failed ? 'fail' : ''} />
        ))}
    </span>
);

export const ScenariosPage = () => {
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const { lab, loadedAt } = useCockpit();
    const [start, setStart] = useState<{ suite: string } | null>(null);
    const [preflight, setPreflight] = useState<ScenarioPreflightReport>();
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState<string>();
    const scope = lab ? `${lab.customer}/${lab.environment}` : '';

    // Both remembered: opening the page again shows the last lists at once and refreshes them behind it.
    const runs = usePoll<ScenarioRunList>(
        axios && lab ? () => new ScenarioRunClient(undefined, axios).scenarioRuns(lab.customer, lab.environment, 15) : null,
        8000,
        [axios, lab?.customer, lab?.environment],
        `scenario-runs/${scope}`
    );
    const suites = usePoll<ScenarioSuiteSummary[]>(
        axios && lab ? () => new ScenarioRunClient(undefined, axios).scenarioSuites(lab.customer, lab.environment) : null,
        60000,
        [axios, lab?.customer, lab?.environment],
        `scenario-suites/${scope}`
    );

    if (!lab) {
        return (
            <>
                <div className="pagehead"><div><div className="eyebrow">Lab</div><h1 className="h1">Scenario's</h1></div></div>
                {loadedAt ? <Empty>Geen omgeving om scenario's op te draaien. De API noemt ze in ScenarioRuns:StartableEnvironments.</Empty> : <Loading />}
            </>
        );
    }

    const current = (runs.data?.runs ?? []).find((r) => !r.finishedAtUtc && !r.stale);

    const openStart = async (suite: string) => {
        setStart({ suite });
        setPreflight(undefined);
        setError(undefined);
        if (!axios) return;
        try {
            setPreflight(await checkScenarioPreflight(axios, lab.customer, lab.environment, suite));
        } catch (e) {
            setError(apiError(e));
        }
    };

    const doStart = async () => {
        if (!axios || !start) return;
        setStarting(true);
        try {
            const started = await new ScenarioRunClient(undefined, axios).startScenarioRun(lab.customer, lab.environment, { suite: start.suite, dryRun: false });
            if (started.runId) navigate(v2.run(lab.customer, lab.environment, started.runId));
        } catch (e: any) {
            setError(e?.response?.status === 409 ? 'Er loopt al een run op deze omgeving' : apiError(e));
        } finally {
            setStarting(false);
        }
    };

    const blocked = (preflight?.checks ?? []).some((c) => preflightTone(c.status) === 'bad');

    return (
        <>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">Lab · {lab.customer} {lab.environment}</div>
                    <h1 className="h1">Scenario's <EnvChip kind="lab" environment={`${lab.customer} ${lab.environment}`} /></h1>
                </div>
                <div className="hrow" style={{ gap: 10 }}>
                    {runs.data?.startBlockedReason && <span className="chip warn">{runs.data.startBlockedReason}</span>}
                    {(runs.loading || suites.loading) && runs.data && (
                        <span className="mut hrow" style={{ fontSize: 12, gap: 6 }}>
                            <Spinner />
                            verversen
                        </span>
                    )}
                </div>
            </div>

            {current && (
                <section className="card" style={{ borderColor: '#1F5F58', background: '#0B1716' }}>
                    <div className="ch" style={{ borderBottomColor: '#163B37' }}>
                        <span className="ct"><Spinner />Draait nu</span>
                        <Link className="btn sm" to={v2.run(lab.customer, lab.environment, current.runId!)}>Volgen</Link>
                    </div>
                    <div className="cb g3">
                        <Kv rows={[['Suite', current.suiteName ?? '–'], ['Run', <span className="mono">{current.runId}</span>]]} />
                        <Kv rows={[['Build', <span className="mono">{shortBuild(current.buildVersion) || '–'}</span>], ['Gestart', `${ago(current.startedAtUtc)} geleden`]]} />
                        <div>
                            <div className="hrow mono" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                                <span>{(current.totals?.passed ?? 0) + (current.totals?.failed ?? 0)} van {current.totals?.total ?? 0}</span>
                            </div>
                            <div style={{ marginTop: 8 }}><Segments passed={current.totals?.passed} failed={current.totals?.failed} total={current.totals?.total} /></div>
                        </div>
                    </div>
                </section>
            )}

            <div className="g2e">
                <Card title="Suites" right={<span className="mut" style={{ fontSize: 12 }}>laatste uitkomst</span>}>
                    <ErrorNotice error={suites.error} />
                    {suites.pending && <Loading>Suites ophalen…</Loading>}
                    {(suites.data ?? []).map((s) => {
                        const last = (runs.data?.runs ?? []).find((r) => r.suiteName === s.name);
                        return (
                            <div key={s.id ?? s.name} className="li" style={{ gridTemplateColumns: 'minmax(0,1fr) 130px auto', cursor: 'default' }}>
                                <span>
                                    <span className="t">{s.name}</span>
                                    <span className="m">
                                        <span>{s.scenarios?.length ?? 0} scenario's</span>
                                        {last?.startedAtUtc && <span className="mono">{when(last.startedAtUtc)}</span>}
                                        {s.problem && <span className="bad">{s.problem}</span>}
                                    </span>
                                </span>
                                {last ? (
                                    <Link to={v2.run(lab.customer, lab.environment, last.runId!)} title="Laatste run openen">
                                        <Segments passed={last.totals?.passed} failed={last.totals?.failed} total={last.totals?.total} />
                                    </Link>
                                ) : (
                                    <span className="mut" style={{ fontSize: 12 }}>nog niet gedraaid</span>
                                )}
                                <button className="btn sm" disabled={!runs.data?.canStart || !!current} onClick={() => openStart(s.name!)}>Start</button>
                            </div>
                        );
                    })}
                </Card>

                <Card title="Recente runs">
                    <ErrorNotice error={runs.error} />
                    {runs.pending && <Loading>Runs ophalen…</Loading>}
                    {(runs.data?.runs ?? []).map((r) => {
                        const secs = r.startedAtUtc ? Math.round(((r.finishedAtUtc ? new Date(r.finishedAtUtc).getTime() : Date.now()) - new Date(r.startedAtUtc).getTime()) / 1000) : 0;
                        return (
                            <Link key={r.runId} className="li" style={{ gridTemplateColumns: 'minmax(0,1fr) auto auto' }} to={v2.run(lab.customer, lab.environment, r.runId!)}>
                                <span>
                                    <span className="t">{r.suiteName}{r.dryRun ? ' · proefdraai' : ''}</span>
                                    <span className="m">
                                        <span className="mono">{r.startedAtUtc ? when(r.startedAtUtc) : ''}</span>
                                        <span className="mono">{shortBuild(r.buildVersion)}</span>
                                        <span className="mono">{duration(secs)}</span>
                                    </span>
                                </span>
                                <span className={runTone(r.totals?.failed, r.finishedAtUtc, r.totals?.passed, r.totals?.total)}>
                                    {!r.finishedAtUtc && !r.stale && <Spinner />}
                                    {r.totals?.passed ?? 0} / {r.totals?.total ?? 0}
                                    {r.totals?.failed ? ` · ${r.totals.failed} rood` : ''}
                                </span>
                                <span className="mut">›</span>
                            </Link>
                        );
                    })}
                </Card>
            </div>

            {start && (
                <>
                    <button className="scrim dialog-scrim" aria-label="Annuleren" onClick={() => setStart(null)} />
                    <div className="dialog" role="dialog" aria-label="Run starten">
                        <div className="ch">
                            <span className="h2">{start.suite} starten</span>
                            <EnvChip kind="lab" environment={`${lab.customer} ${lab.environment}`} />
                        </div>
                        <div className="cb">
                            <div className="eyebrow" style={{ marginBottom: 6 }}>Vooraf gecontroleerd</div>
                            {!preflight && !error && <span className="hrow mut"><Spinner />Nakijken…</span>}
                            <div className="checks">
                                {(preflight?.checks ?? []).map((c, i) => (
                                    <CheckRow key={c.id ?? i} tone={preflightTone(c.status)} label={c.name ?? c.id} value={c.actual ?? c.status} />
                                ))}
                            </div>
                            {error && <div className="bad" style={{ marginTop: 8 }}>{error}</div>}
                        </div>
                        <div className="ch" style={{ borderTop: '1px solid #1A212B', borderBottom: 0, justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => setStart(null)}>Annuleren</button>
                            <button className="btn pri" disabled={starting || !preflight || blocked} onClick={doStart}>{starting ? <Spinner /> : null}Start en volg</button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
